// ─────────────────────────────────────────────────────────────────────────────
// Sección "Recap" del Admin (módulo `wrapped` del demo, fundamentales-admin.js : 1224).
//
// Paridad demo, bloque a bloque: edición + nombre visible + nueva edición · alcance OU, interruptor
// y ventana de visibilidad + estado de la notificación · tabla de slides con vista previa, título y
// activo (editor por slide en modal) · caja de difusión · mensaje genérico de respaldo (imagen +
// textos ×7) · historial de ediciones con sus ganadores anuales.
//
// Datos REALES (v1/admin/recap). Un único botón Guardar: el PUT reemplaza la edición de forma
// atómica, igual que Gestión — nada persiste sin Guardar. El conteo de ganadores del historial es el
// de la colección `winners` del año; si no hay, es 0 (nunca relleno). Estados honestos
// (loading/error/vacío) y CERO mocks.
//
// Permisos: la matriz del cliente reserva "Gestionar Recap" a Admin General y Admin Fundamentales;
// el backend responde 403 al resto y aquí se muestra el mismo bloqueo antes de pedir datos.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  useRecapEditions, useRecapEdition, useSaveRecapEdition, useCreateRecapEdition,
  useNotifyRecapEdition, getRecognitionErrorMessage,
} from '@/api/recognition';
import type { RecapEditionDto, RecapSlideDto, LocalizedText } from '@/api/recognition';
import { useUnsavedChangesGuard } from '@/contexts/UnsavedChangesContext';
import { RoleGuard } from '@/auth/RoleGuard';
import { StateBlock } from '../components';
import { AdminSection, AdminSaveBar, AdminPagination, usePagination, LocalizedFields, hasAllLanguages } from './adminShared';
import { RecapSlideEditor } from './RecapSlideEditor';

/** Mismas OUs que el resto del admin (campañas/roles) y que el selector del demo. */
const SCOPE_OPTIONS = ['México', 'Europa', 'LATAM', 'US', 'FOSE', 'Growth'];

/** El slide de ganador toma su icono de la insignia real configurada en Ganadores. */
const AUTO_ICON_KEY = 'ganador';

/** `datetime-local` ⇄ ISO. El input no admite zona, así que se envía como hora local del admin. */
const toLocalInput = (iso?: string | null): string => (iso ? iso.slice(0, 16) : '');
const fromLocalInput = (value: string): string | null => (value ? new Date(value).toISOString() : null);

const hasAnyText = (value: LocalizedText): boolean => Object.values(value).some((v) => (v ?? '').trim().length > 0);

export function AdminRecap() {
  return (
    <RoleGuard
      allow={['AdminGlobal', 'AdminBrillamos']}
      fallback={<RecapDenied />}
    >
      <RecapContent />
    </RoleGuard>
  );
}

function RecapDenied() {
  const { t } = useTranslation('recognition');
  return (
    <AdminSection title={t('admin.recap.title')} description={t('admin.recap.desc')}>
      <div className="adm-denied" role="alert"><i className="fas fa-lock" /> {t('admin.recap.deniedRole')}</div>
    </AdminSection>
  );
}

function RecapContent() {
  const { t, i18n } = useTranslation('recognition');
  const editions = useRecapEditions();
  const [year, setYear] = useState<number | undefined>();
  const [draft, setDraft] = useState<RecapEditionDto | null>(null);
  const [dirty, setDirty] = useState(false);
  useUnsavedChangesGuard(dirty);
  const [editing, setEditing] = useState<RecapSlideDto | null>(null);
  const [preview, setPreview] = useState(false);

  // Año seleccionado: el más reciente hasta que el admin elija otro.
  const selectedYear = year ?? editions.data?.[0]?.anio;
  const edition = useRecapEdition(selectedYear);
  const save = useSaveRecapEdition();
  const create = useCreateRecapEdition();
  const notify = useNotifyRecapEdition();

  // Sincroniza el borrador cuando llega/cambia la edición.
  if (edition.data && (draft === null || draft.anio !== edition.data.anio)) {
    setDraft(edition.data);
    setDirty(false);
  }

  const patch = (p: Partial<RecapEditionDto>) => { setDraft((d) => (d ? { ...d, ...p } : d)); setDirty(true); };

  const patchSlide = (next: RecapSlideDto) => {
    setDraft((d) => (d ? { ...d, slides: d.slides.map((s) => (s.clave === next.clave ? next : s)) } : d));
    setDirty(true);
  };

  const pager = usePagination(draft?.slides ?? [], 10);

  const activeSlides = draft?.slides.filter((s) => s.activo) ?? [];
  const slidesOk = activeSlides.length > 0 && activeSlides.every((s) => hasAllLanguages(s.titulo));
  const msg = draft?.mensajeGenerico;
  const genericTouched = Boolean(msg && (hasAnyText(msg.titulo) || hasAnyText(msg.descripcion)));
  const genericOk = !genericTouched || Boolean(msg && hasAllLanguages(msg.titulo) && hasAllLanguages(msg.descripcion));
  const canSave = Boolean(draft?.nombre.trim()) && slidesOk && genericOk;

  const doSave = () => {
    if (!draft) return;
    save.mutate(draft, { onSuccess: () => setDirty(false) });
  };

  const doCreate = () => {
    const anio = (editions.data?.[0]?.anio ?? new Date().getFullYear() - 1) + 1;
    create.mutate({ anio }, { onSuccess: (created) => { setYear(created.anio); setDraft(created); setDirty(false); } });
  };

  const slideName = (clave: string) => t(`admin.recap.slides.${clave}.name`);
  const slideHint = (clave: string) => t(`admin.recap.slides.${clave}.hint`);
  const titleFor = (s: RecapSlideDto) => s.titulo[i18n.language] ?? s.titulo.es ?? '';

  return (
    <AdminSection title={t('admin.recap.title')} description={t('admin.recap.desc')}>
      <StateBlock
        isLoading={editions.isLoading || edition.isLoading}
        isError={editions.isError || edition.isError}
        error={editions.error ?? edition.error}
        isEmpty={!editions.isLoading && (editions.data?.length ?? 0) === 0}
        emptyText={t('admin.recap.noEditions')}
        onRetry={() => { editions.refetch(); edition.refetch(); }}
      >
        {/* `selectedYear` manda: si la lista de ediciones se queda vacía, no se sigue mostrando
            el borrador de una edición que ya no está. */}
        {draft && selectedYear === draft.anio && (
          <>
            <AdminSaveBar
              onSave={doSave} saving={save.isPending} dirty={dirty && canSave} saved={save.isSuccess}
              disabledReason={dirty && !canSave ? t('admin.recap.fixValidation') : null}
            />
            {save.isError && (
              <p className="adm-error" role="alert">
                <i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(save.error, t('admin.saveError'))}
              </p>
            )}

            {/* ── Edición ─────────────────────────────────────────────── */}
            <div className="adm-recap-row">
              <label className="adm-field">
                <span>{t('admin.recap.edition')}</span>
                <select className="field"
                  value={selectedYear ?? ''}
                  onChange={(e) => { setYear(Number(e.target.value)); setDraft(null); }}
                >
                  {editions.data?.map((e) => <option key={e.anio} value={e.anio}>{e.anio}</option>)}
                </select>
              </label>

              <label className="adm-field adm-recap-name">
                <span>{t('admin.recap.editionName')}</span>
                <input type="text" value={draft.nombre} onChange={(e) => patch({ nombre: e.target.value })} />
              </label>

              <button type="button" className="btn btn--primary" onClick={doCreate} disabled={create.isPending}>
                <i className="fas fa-plus" /> {t('admin.recap.newEdition')}
              </button>
            </div>
            {create.isError && (
              <p className="adm-error" role="alert">
                <i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(create.error, t('admin.saveError'))}
              </p>
            )}

            {/* ── WP-F7 / RN-PR10 · lock de edición durante la difusión ──────────────────────────
                🔴 El aviso «tu Recap ya está listo» hace una promesa concreta: a QUIÉN y ENTRE QUÉ
                FECHAS. Desde ese momento el servidor rechaza ENCOGERLA —quitar OUs del alcance,
                retrasar el inicio, adelantar el fin—, y permite ampliarla.

                Los campos NO se deshabilitan, a propósito: ampliar es legítimo y bloquearlos dejaría
                atrapado a quien anuncia con fin el 31-dic y luego necesita alargar. Y la regla NO se
                replica aquí: una segunda copia derivaría de la del contrato en cuanto una se tocara
                —es lo mismo que se decidió en `F1` con RN-R03—. El servidor es la autoridad y su
                mensaje ya se pinta; esta nota existe para que nadie descubra la regla por un error. */}
            {draft.notificadoAt && (
              <p className="adm-note adm-note--info" role="note">
                <i className="fas fa-lock" /> {t('admin.recap.difusionLockHint')}
              </p>
            )}

            {/* ── Alcance, interruptor y ventana de visibilidad ────────── */}
            <div className="adm-recap-row">
              <label className="adm-field">
                <span>{t('admin.recap.scope')}</span>
                <select className="field"
                  value={draft.alcance[0] ?? ''}
                  onChange={(e) => patch({ alcance: e.target.value ? [e.target.value] : [] })}
                >
                  <option value="">{t('admin.recap.scopeGlobal')}</option>
                  {SCOPE_OPTIONS.map((ou) => <option key={ou} value={ou}>{ou}</option>)}
                </select>
              </label>

              <div className="adm-field">
                <span>{t('admin.recap.active')}</span>
                <label className="adm-inline-toggle">
                  <input type="checkbox" checked={draft.activo} onChange={(e) => patch({ activo: e.target.checked })} />
                  {draft.activo ? t('admin.recap.activeOn') : t('admin.recap.activeOff')}
                </label>
              </div>

              <label className="adm-field">
                <span>{t('admin.recap.visibleFrom')}</span>
                <input type="datetime-local" value={toLocalInput(draft.visibleDesde)}
                  onChange={(e) => patch({ visibleDesde: fromLocalInput(e.target.value) })} />
              </label>

              <label className="adm-field">
                <span>{t('admin.recap.visibleTo')}</span>
                <input type="datetime-local" value={toLocalInput(draft.visibleHasta)}
                  onChange={(e) => patch({ visibleHasta: fromLocalInput(e.target.value) })} />
              </label>

              <div className="adm-field">
                <span>{t('admin.recap.notification')}</span>
                {draft.notificadoAt ? (
                  <span className="adm-status adm-status--on">
                    <i className="fas fa-check-circle" />{' '}
                    {t('admin.recap.notifiedAt', { date: new Date(draft.notificadoAt).toLocaleDateString(i18n.language) })}
                  </span>
                ) : (
                  <span className="adm-status adm-status--off">{t('admin.recap.neverNotified')}</span>
                )}
              </div>
            </div>

            {/* ── Slides ───────────────────────────────────────────────── */}
            <h3 className="adm-subtitle">{t('admin.recap.slidesTitle', { count: draft.slides.length })}</h3>
            <p className="adm-note">{t('admin.recap.slidesHint')}</p>
            <p className="adm-note adm-note--info"><i className="fas fa-globe" /> {t('admin.recap.i18nHint')}</p>

            <div className="adm-table-wrap">
              <table className="adm-table adm-table--glass">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('admin.recap.colSlide')}</th>
                    <th>{t('admin.recap.colPreview')}</th>
                    <th>{t('admin.recap.colTitle')}</th>
                    <th>{t('admin.recap.colActive')}</th>
                    <th>{t('admin.recap.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pager.slice.map((s) => (
                    <tr key={s.clave}>
                      <td className="adm-recap-index">{s.indice}</td>
                      <td>
                        <strong>{slideName(s.clave)}</strong>
                        <span className="adm-recap-slide-hint">{slideHint(s.clave)}</span>
                      </td>
                      <td>
                        <span className="adm-recap-swatch-cell" style={{ background: s.fondo }}>
                          {s.clave === AUTO_ICON_KEY ? <i className="fas fa-award" /> : s.icono}
                        </span>
                      </td>
                      <td>
                        <span className={titleFor(s) ? '' : 'adm-recap-missing'}>
                          {titleFor(s) || t('admin.recap.titleMissing')}
                        </span>
                        {s.activo && !hasAllLanguages(s.titulo) && (
                          <span className="adm-recap-missing"><i className="fas fa-language" /> {t('admin.recap.needAllLangs')}</span>
                        )}
                      </td>
                      <td>
                        <label className="adm-inline-toggle">
                          <input type="checkbox" checked={s.activo}
                            onChange={(e) => patchSlide({ ...s, activo: e.target.checked })} />
                        </label>
                      </td>
                      <td>
                        <button type="button" className="btn btn--link" onClick={() => setEditing(s)}>
                          <i className="fas fa-pen" /> {t('admin.recap.edit')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <AdminPagination page={pager.page} totalPages={pager.totalPages} pageSize={pager.pageSize}
              onPage={pager.setPage} onPageSize={pager.setPageSize} />

            {/* ── Difusión ─────────────────────────────────────────────── */}
            <div className="adm-recap-notify">
              <div>
                <h4><i className="fas fa-bell" /> {t('admin.recap.notifyTitle')}</h4>
                <p>{t('admin.recap.notifyDesc', { year: draft.anio })}</p>
              </div>
              <div className="adm-recap-notify-actions">
                <button type="button" className="btn btn--secondary" onClick={() => setPreview(true)}>
                  <i className="fas fa-eye" /> {t('admin.recap.preview')}
                </button>
                <button type="button" className="btn btn--primary" disabled={!draft.activo || notify.isPending}
                  onClick={() => notify.mutate(draft.anio)}>
                  <i className="fas fa-paper-plane" /> {t('admin.recap.send')}
                </button>
              </div>
            </div>
            {!draft.activo && <p className="adm-note adm-note--warn"><i className="fas fa-circle-info" /> {t('admin.recap.notifyNeedsActive')}</p>}
            {notify.isError && (
              <p className="adm-error" role="alert">
                <i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(notify.error, t('admin.recap.notifyError'))}
              </p>
            )}

            {/* ── Mensaje genérico de respaldo ─────────────────────────── */}
            <h3 className="adm-subtitle">{t('admin.recap.genericTitle')}</h3>
            <p className="adm-note">{t('admin.recap.genericDesc')}</p>
            <div className="adm-recap-generic">
              <div className="adm-recap-generic-image">
                <input type="url" value={draft.mensajeGenerico.imagenUrl ?? ''}
                  placeholder={t('admin.recap.imageUrlPlaceholder')}
                  onChange={(e) => patch({
                    mensajeGenerico: { ...draft.mensajeGenerico, imagenUrl: e.target.value || null },
                  })} />
                {draft.mensajeGenerico.imagenUrl
                  ? <img src={draft.mensajeGenerico.imagenUrl} alt="" className="adm-recap-generic-preview" />
                  : <div className="adm-recap-generic-preview adm-recap-generic-preview--empty"><i className="fas fa-image" /></div>}
              </div>
              <div className="adm-recap-generic-fields">
                <LocalizedFields
                  label={t('admin.recap.genericTitleLabel')}
                  value={draft.mensajeGenerico.titulo}
                  requireAll={genericTouched}
                  onChange={(titulo) => patch({ mensajeGenerico: { ...draft.mensajeGenerico, titulo } })}
                />
                <LocalizedFields
                  label={t('admin.recap.genericDescLabel')}
                  value={draft.mensajeGenerico.descripcion}
                  textarea
                  requireAll={genericTouched}
                  onChange={(descripcion) => patch({ mensajeGenerico: { ...draft.mensajeGenerico, descripcion } })}
                />
                <p className="adm-note adm-note--info"><i className="fas fa-circle-info" /> {t('admin.recap.genericNote')}</p>
                {genericTouched && !genericOk && (
                  <p className="adm-note adm-note--warn"><i className="fas fa-language" /> {t('admin.recap.needAllLangs')}</p>
                )}
              </div>
            </div>

            {/* ── Historial ────────────────────────────────────────────── */}
            <h3 className="adm-subtitle">{t('admin.recap.historyTitle')}</h3>
            <p className="adm-note">{t('admin.recap.historyDesc')}</p>
            <div className="adm-table-wrap">
              <table className="adm-table adm-table--glass">
                <thead>
                  <tr>
                    <th>{t('admin.recap.colEdition')}</th>
                    <th>{t('admin.recap.colYear')}</th>
                    <th>{t('admin.recap.colScope')}</th>
                    <th>{t('admin.recap.colSlides')}</th>
                    <th>{t('admin.recap.colWinners')}</th>
                    <th>{t('admin.recap.colStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {editions.data?.map((e) => (
                    <tr key={e.anio} className={e.anio === draft.anio ? 'adm-row--selected' : ''}>
                      <td><strong>{e.nombre}</strong></td>
                      <td>{e.anio}</td>
                      <td>{e.alcance.length ? e.alcance.join(', ') : t('admin.recap.scopeGlobal')}</td>
                      <td>{e.slides}</td>
                      <td>
                        <span className="adm-recap-winners">
                          <span className="adm-recap-winners-badge">🏆</span>
                          {t('admin.recap.winnersCount', { count: e.ganadoresAnuales })}
                        </span>
                      </td>
                      <td>
                        <span className={`adm-status ${e.estado === 'activa' ? 'adm-status--on' : 'adm-status--off'}`}>
                          {t(`admin.recap.status.${e.estado}`)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {editing && (
              <RecapSlideEditor
                slide={editing}
                autoIcon={editing.clave === AUTO_ICON_KEY}
                onCancel={() => setEditing(null)}
                onSave={(next) => { patchSlide(next); setEditing(null); }}
              />
            )}

            {preview && <RecapPreview edition={draft} onClose={() => setPreview(false)} />}
          </>
        )}
      </StateBlock>

      {/* Sin ediciones todavía: el estado vacío ofrece crear la primera (nada precargado). */}
      {!editions.isLoading && !editions.isError && (editions.data?.length ?? 0) === 0 && (
        <button type="button" className="btn btn--primary" onClick={doCreate} disabled={create.isPending}>
          <i className="fas fa-plus" /> {t('admin.recap.createFirst')}
        </button>
      )}
    </AdminSection>
  );
}

/** Vista previa de la edición tal como la recorrerá el usuario (solo slides activos). */
function RecapPreview({ edition, onClose }: Readonly<{ edition: RecapEditionDto; onClose: () => void }>) {
  const { t, i18n } = useTranslation('recognition');
  const slides = edition.slides.filter((s) => s.activo);
  // createPortal a document.body: `.plataforma-main` (recognition-shell.css) lleva `isolation:
  // isolate`, que aísla el z-index de `.modal-overlay` dentro de su propio contexto de apilamiento
  // — nunca compite contra el z-index explícito del Sidebar, que queda pintado encima. Mismo fix ya
  // aplicado en BulkUploadModal.tsx/WinnersImportWizardModal.tsx.
  return createPortal((
    <div className="modal-overlay modal-overlay--fade" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="adm-recap-preview-modal" onClick={(e) => e.stopPropagation()}>
        <button className="rec-modal-close" onClick={onClose} aria-label={t('common.close')}>
          <i className="fas fa-times" />
        </button>
        <h3 className="adm-recap-editor-title">{edition.nombre}</h3>
        <p className="adm-note">{t('admin.recap.previewNote')}</p>
        <div className="adm-recap-preview-strip">
          {slides.map((s) => (
            <div key={s.clave} className="adm-recap-slide-preview" style={{ background: s.fondo }}>
              <span className="adm-recap-slide-icon">
                {s.clave === 'ganador' ? <i className="fas fa-award" /> : s.icono}
              </span>
              <p className="adm-recap-slide-title">{s.titulo[i18n.language] ?? s.titulo.es ?? ''}</p>
              {(s.etiquetasNumero ?? []).length > 0 && (
                <div className="adm-recap-slide-numbers">
                  {(s.etiquetasNumero ?? []).map((label) => (
                    <div key={label}><strong>—</strong><span>{label}</span></div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  ), document.body);
}

export default AdminRecap;
